import React from "react";
import { Box, CircularProgress, Divider, Option } from "@mui/joy";
import type { Company } from "../../../api/auth/Auth.typed";
import { CompanyModel } from "../../../api/company/CompanyModel";
import type { ZekayConfig } from "../../../api/config/Config.typed";
import useAppSettings from "../../../contexts/app/appSettings";
import { AppConfigContext } from "../../../contexts/app/appConfig";
import { useAuth } from "../../../contexts/api/auth";
import { useAlert } from "../../../contexts/app/notificationToast";
import DarkTypography from "../../common/DarkTypography";
import DarkInput from "../../common/DarkInput";
import DarkSelect from "../../common/DarkSelect";
import IconButtonCTA from "../../common/IconButtonCTA";
import { SDelete, CloudUploadIcon } from "../../../styles/StyledMUI";
import { FALLBACK_COMPANY_TIME_ZONE, listIanaTimeZones } from "../../../utils/companyTimeZone";
import SettingsSectionCard from "../../shared/settingsSection/SettingsSectionCard";
import SettingsSectionEditToolbar from "../../shared/settingsSection/SettingsSectionEditToolbar";
import { ColorSwatch } from "../../common/ColorPalette";
import {
  applyZekayThemePreset,
  normalizePresetFromApi,
  themePresetDominantSwatchHex,
  ZEKAY_THEME_PRESET_OPTIONS,
} from "../../../styles/themePresets";

function selectTzRaw(timeZoneRaw: string | undefined): string {
  return timeZoneRaw?.trim() ? timeZoneRaw : FALLBACK_COMPANY_TIME_ZONE;
}

type ConfigSliceDraft = {
  timeZone: string;
  logoUrl: string;
  qrUrl: string;
};

type EntrepriseDraftPayload = {
  config: ConfigSliceDraft;
  company: Company;
};

type Props = {
  config: ZekayConfig;
  IsUploadingLogo: false | "logo" | "qr";
  OnUploadLogo: (file: File | null) => Promise<void>;
  OnUploadQr: (file: File | null) => Promise<void>;
  OnRemoveLogo: () => void;
  OnRemoveQr: () => void;
  canEditBackend: boolean;
};

function buildEntrepriseDraft(cfg: ZekayConfig, comp: Company): EntrepriseDraftPayload {
  return {
    config: {
      timeZone: selectTzRaw(cfg.TimeZone),
      logoUrl: cfg.LogoUrl || "",
      qrUrl: cfg.LogoQrCodeUrl || "",
    },
    company: { ...comp },
  };
}

export default function CentreEntrepriseSection({
  config,
  IsUploadingLogo,
  OnUploadLogo,
  OnUploadQr,
  OnRemoveLogo,
  OnRemoveQr,
  canEditBackend,
}: Props) {
  const appConfig = React.useContext(AppConfigContext);
  const { user } = useAuth();
  const { saveConfig } = useAppSettings();
  const { showAlert } = useAlert();

  const tenantCompanyId = user?.Role?.company_id ?? user?.company_id;

  const companyModel = React.useMemo(
    () => (appConfig != null ? new CompanyModel(appConfig) : null),
    [appConfig],
  );

  const zones = React.useMemo(() => listIanaTimeZones(), []);

  const [company, setCompany] = React.useState<Company | null>(null);
  const [companyLoading, setCompanyLoading] = React.useState(true);
  const [companyLoadFailed, setCompanyLoadFailed] = React.useState(false);

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<EntrepriseDraftPayload | null>(null);

  React.useEffect(() => {
    if (companyModel == null) return;
    let cancelled = false;
    setCompanyLoading(true);
    setCompanyLoadFailed(false);

    void (async () => {
      try {
        const c = await companyModel.getCurrentCompany();
        if (cancelled) return;
        if (!c?.id || c.id <= 0) {
          setCompany(null);
          setCompanyLoadFailed(true);
        } else {
          setCompany(c);
          setCompanyLoadFailed(false);
        }
      } catch {
        if (!cancelled) {
          setCompany(null);
          setCompanyLoadFailed(true);
        }
      } finally {
        if (!cancelled) setCompanyLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyModel, tenantCompanyId]);

  const startEdit = () => {
    if (company == null) return;

    setDraft(buildEntrepriseDraft(config, company));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditing(false);
  };

  React.useEffect(() => {
    if (!editing) return;
    setDraft((prev) =>
      prev == null
        ? null
        : {
            ...prev,
            config: {
              ...prev.config,
              logoUrl: config.LogoUrl || "",
              qrUrl: config.LogoQrCodeUrl || "",
            },
          },
    );
  }, [config.LogoUrl, config.LogoQrCodeUrl, editing]);

  const save = React.useCallback(async () => {
    if (draft == null || companyModel == null || saving) return;

    setSaving(true);
    try {
      await saveConfig({
        ...config,
        TimeZone: draft.config.timeZone,
        LogoUrl: draft.config.logoUrl,
        LogoQrCodeUrl: draft.config.qrUrl,
      });
      await companyModel.updateCompany(draft.company);

      const fresh = await companyModel.getCurrentCompany();
      setCompany(fresh?.id ? fresh : draft.company);

      applyZekayThemePreset(normalizePresetFromApi(fresh?.ui_theme_preset));

      showAlert("Enregistré", "success");
      cancelEdit();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Échec de l'enregistrement";
      showAlert(msg, "danger");
    } finally {
      setSaving(false);
    }
  }, [draft, companyModel, config, saveConfig, showAlert, saving]);

  const viewCfgSlice: ConfigSliceDraft = {
    timeZone: selectTzRaw(config.TimeZone),
    logoUrl: config.LogoUrl || "",
    qrUrl: config.LogoQrCodeUrl || "",
  };

  const cfgSlice = editing && draft !== null ? draft.config : viewCfgSlice;
  const compView = editing && draft !== null ? draft.company : company;

  const fieldsEnabled = editing && canEditBackend;
  const hasLogoUrl = Boolean((cfgSlice.logoUrl || "").trim());
  const hasQrUrl = Boolean((cfgSlice.qrUrl || "").trim());

  const toolbarEligible =
    canEditBackend &&
    company != null &&
    !companyLoading &&
    !companyLoadFailed &&
    Boolean(company?.id && company.id > 0);

  return (
    <SettingsSectionCard
      title="Entreprise"
      titleEnd={
        <SettingsSectionEditToolbar
          canEditBackend={toolbarEligible}
          isEditing={editing}
          saving={saving}
          onEdit={startEdit}
          onCancel={cancelEdit}
          onSave={() => void save()}
        />
      }
    >
      {companyLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size="sm" />
        </Box>
      ) : companyLoadFailed || company == null ? (
        <DarkTypography level="body-md" colorVariant="muted">
          Impossible de charger les données de la société.
        </DarkTypography>
      ) : (
        <>
          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Coordonnées
          </DarkTypography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 160px) 1fr" },
              gap: 2,
              alignItems: "center",
              maxWidth: { xs: "95vw", sm: "none" },
            }}
          >
            <DarkTypography level="body-md" colorVariant="primary">
              Raison sociale
            </DarkTypography>
            <DarkInput
              value={compView?.name || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, name: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Adresse
            </DarkTypography>
            <DarkInput
              value={compView?.address || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, address: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Code postal
            </DarkTypography>
            <DarkInput
              value={compView?.postal_code || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, postal_code: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Ville
            </DarkTypography>
            <DarkInput
              value={compView?.city || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, city: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Pays
            </DarkTypography>
            <DarkInput
              value={compView?.country || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, country: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Téléphone
            </DarkTypography>
            <DarkInput
              value={compView?.phone || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, phone: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Email
            </DarkTypography>
            <DarkInput
              type="email"
              placeholder="contact@entreprise.fr"
              value={compView?.mail_address || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, mail_address: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              SIRET
            </DarkTypography>
            <DarkInput
              placeholder="123 456 789 00012"
              value={compView?.siret || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, siret: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
            <DarkTypography level="body-md" colorVariant="primary">
              Code APE
            </DarkTypography>
            <DarkInput
              placeholder="9319Z"
              value={compView?.ape || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((prev) =>
                  prev == null
                    ? null
                    : { ...prev, company: { ...prev.company, ape: e.target.value } },
                )
              }
              disabled={!fieldsEnabled}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Fuseau horaire
          </DarkTypography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <DarkSelect
              placeholder="Fuseau"
              value={cfgSlice.timeZone}
              onChange={(_, v) => {
                if (!fieldsEnabled) return;
                setDraft((prev) =>
                  prev == null
                    ? null
                    : {
                        ...prev,
                        config: {
                          ...prev.config,
                          timeZone: typeof v === "string" ? v : FALLBACK_COMPANY_TIME_ZONE,
                        },
                      },
                );
              }}
              disabled={!fieldsEnabled}
              sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: "100%" }}
            >
              {zones.map((z) => (
                <Option key={z} value={z}>
                  {z}
                </Option>
              ))}
            </DarkSelect>
          </Box>

          <Divider sx={{ my: 2 }} />

          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Apparence
          </DarkTypography>
          <DarkSelect
            placeholder="Thème"
            value={normalizePresetFromApi(compView?.ui_theme_preset)}
            onChange={(_, v) => {
              if (!fieldsEnabled) return;
              const next = typeof v === "string" ? v : "ocean_blue";
              setDraft((prev) =>
                prev == null
                  ? null
                  : { ...prev, company: { ...prev.company, ui_theme_preset: next } },
              );
            }}
            renderValue={(option) => {
              const id = normalizePresetFromApi(String(option?.value ?? ""));
              const label = ZEKAY_THEME_PRESET_OPTIONS.find((x) => x.id === id)?.label ?? "";
              const hex = themePresetDominantSwatchHex(id);
              return (
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <ColorSwatch
                    hex={hex}
                    size={14}
                    fallback={themePresetDominantSwatchHex("ocean_blue")}
                  />
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {label}
                  </Box>
                </Box>
              );
            }}
            disabled={!fieldsEnabled}
            sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: "100%" }}
          >
            {ZEKAY_THEME_PRESET_OPTIONS.map((o) => (
              <Option key={o.id} value={o.id} label={o.label}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <ColorSwatch
                    hex={themePresetDominantSwatchHex(o.id)}
                    size={14}
                    fallback={themePresetDominantSwatchHex("ocean_blue")}
                  />
                  <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                    {o.label}
                  </Box>
                </Box>
              </Option>
            ))}
          </DarkSelect>

          <Divider sx={{ my: 2 }} />

          <DarkTypography level="body-sm" colorVariant="muted" sx={{ mb: 1 }}>
            Logos
          </DarkTypography>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 200px) minmax(0, 1fr) auto" },
                gap: 2,
                alignItems: "center",
                width: "100%",
                maxWidth: { xs: "95vw", sm: "none" },
              }}
            >
              <DarkTypography level="body-md" colorVariant="primary">
                Logo URL (WebP)
              </DarkTypography>
              <DarkInput
                placeholder="https://cdn.example.com/assets/logo.webp"
                value={cfgSlice.logoUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev == null
                      ? null
                      : {
                          ...prev,
                          config: { ...prev.config, logoUrl: e.target.value },
                        },
                  )
                }
                disabled={!fieldsEnabled}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <IconButtonCTA
                  component="label"
                  ctaVariant="accent"
                  disabled={IsUploadingLogo === "logo" || !fieldsEnabled}
                  aria-label="Téléverser le logo WebP"
                >
                  <CloudUploadIcon />
                  <input
                    hidden
                    type="file"
                    accept="image/webp,.webp"
                    onChange={(e) => void OnUploadLogo(e.target.files?.[0] || null)}
                  />
                </IconButtonCTA>
                {hasLogoUrl ? (
                  <IconButtonCTA
                    ctaVariant="danger"
                    disabled={!fieldsEnabled}
                    aria-label="Supprimer le logo"
                    onClick={OnRemoveLogo}
                  >
                    <SDelete />
                  </IconButtonCTA>
                ) : null}
                {IsUploadingLogo === "logo" ? <CircularProgress size="sm" /> : null}
              </Box>

              <DarkTypography level="body-md" colorVariant="primary">
                QR Code URL
              </DarkTypography>
              <DarkInput
                placeholder="https://cdn.example.com/assets/logo_qr.svg"
                value={cfgSlice.qrUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDraft((prev) =>
                    prev == null
                      ? null
                      : {
                          ...prev,
                          config: { ...prev.config, qrUrl: e.target.value },
                        },
                  )
                }
                disabled={!fieldsEnabled}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <IconButtonCTA
                  component="label"
                  ctaVariant="accent"
                  disabled={IsUploadingLogo === "qr" || !fieldsEnabled}
                  aria-label="Téléverser le QR code"
                >
                  <CloudUploadIcon />
                  <input
                    hidden
                    type="file"
                    accept="image/*,.svg"
                    onChange={(e) => void OnUploadQr(e.target.files?.[0] || null)}
                  />
                </IconButtonCTA>
                {hasQrUrl ? (
                  <IconButtonCTA
                    ctaVariant="danger"
                    disabled={!fieldsEnabled}
                    aria-label="Supprimer le logo QR"
                    onClick={OnRemoveQr}
                  >
                    <SDelete />
                  </IconButtonCTA>
                ) : null}
                {IsUploadingLogo === "qr" ? <CircularProgress size="sm" /> : null}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </SettingsSectionCard>
  );
}
